/**
 * CompactBroadcastRow - Spreadsheet-style compact broadcast row with inline editing
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Check, ChevronDown, ChevronUp, RefreshCw, Youtube, Edit2, Archive, 
  Trash2, Play, Trophy, Video, Eye, EyeOff 
} from "lucide-react";
import { formatTime12Hour } from "../utils";
import { VODPanel } from "./VODPanel";

export const CompactBroadcastRow = ({
  broadcast,
  isExpanded,
  isSelected,
  onToggleExpand,
  onToggleSelect,
  onGoLive,
  onEndLive,
  onEdit,
  onDelete,
  onMarkComplete,
  onSyncScores,
  syncingScores,
  onArchiveToOnDemand,
  archiving,
  vodForm,
  setVodForm,
  savingVod,
  onSaveVod,
  onRefreshBroadcast,
  onToggleHidden
}) => {
  const isLive = broadcast.is_live;
  const isCompleted = broadcast.is_completed;
  const hasYouTubeVod = broadcast.youtube_vod_url;
  const hasRecording = broadcast.recording_url;
  
  // Format date compactly
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  return (
    <div className="border-b border-white/5 last:border-b-0">
      {/* Compact Row */}
      <div 
        className={`grid grid-cols-[30px_auto_1fr_100px_90px_80px_100px_auto] gap-2 items-center px-3 py-2 text-xs hover:bg-zinc-800/30 cursor-pointer ${
          isLive ? 'bg-red-500/10' : isSelected ? 'bg-orange-500/10' : isExpanded ? 'bg-zinc-800/50' : ''
        }`}
        onClick={() => onToggleExpand(broadcast.id)}
        data-testid={`broadcast-row-${broadcast.id}`}
      >
        {/* Checkbox */}
        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(broadcast.id)}
            className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
            data-testid={`broadcast-checkbox-${broadcast.id}`}
          />
        </div>
        
        {/* Status Badge */}
        <div className="w-20 flex items-center gap-1">
          <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${
            isLive ? 'bg-red-500 text-white' : 
            isCompleted ? 'bg-green-600/20 text-green-400' :
            'bg-zinc-700 text-zinc-400'
          }`}>
            {isLive ? '● LIVE' : isCompleted ? 'COMPLETED' : 'SCHEDULED'}
          </span>
          {broadcast.is_dummy && (
            <span className="px-1 py-0.5 text-[9px] font-bold uppercase bg-violet-500/20 text-violet-400" data-testid={`dummy-badge-${broadcast.id}`}>DEV</span>
          )}
        </div>
        
        {/* Title & Teams */}
        <div className="min-w-0 flex items-center gap-2">
          <span className="font-medium text-white truncate">{broadcast.title}</span>
          {broadcast.event_id && (
            <Trophy className="w-3 h-3 text-yellow-500 flex-shrink-0" />
          )}
          {hasYouTubeVod && (
            <Youtube className="w-3 h-3 text-red-500 flex-shrink-0" />
          )}
        </div>
        
        {/* Sport */}
        <div className="text-zinc-500">{broadcast.sport}</div>
        
        {/* Date */}
        <div className="text-zinc-400">
          {broadcast.date_tbd ? 'TBD' : formatDate(broadcast.scheduled_date)}
        </div>
        
        {/* Time */}
        <div className="text-zinc-400">
          {broadcast.time_tbd ? 'TBD' : formatTime12Hour(broadcast.scheduled_time)}
        </div>
        
        {/* Score (if completed) */}
        <div className="text-center">
          {isCompleted && broadcast.final_home_score !== null ? (
            <span className="font-bold text-white text-[11px]">
              {broadcast.final_home_score} - {broadcast.final_away_score}
            </span>
          ) : (
            <span className="text-zinc-600">-</span>
          )}
        </div>
        
        {/* Quick Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {!isCompleted && !isLive && (
            <button 
              onClick={() => onGoLive(broadcast.id)} 
              className="p-1 bg-red-600 text-white text-[10px] font-bold uppercase hover:bg-red-500"
              title="Go Live"
            >
              <Play className="w-3 h-3" />
            </button>
          )}
          {isLive && (
            <button 
              onClick={() => onEndLive(broadcast.id)} 
              className="px-2 py-1 bg-zinc-700 text-white text-[10px] font-bold uppercase hover:bg-zinc-600"
            >
              End
            </button>
          )}
          {broadcast.stats_link && (
            <button 
              onClick={() => onSyncScores(broadcast.id)}
              disabled={syncingScores}
              className="p-1 text-zinc-500 hover:text-blue-500 disabled:opacity-50"
              title="Sync scores"
            >
              <RefreshCw className={`w-3 h-3 ${syncingScores ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button 
            onClick={() => onEdit(broadcast)} 
            className="p-1 text-zinc-500 hover:text-white"
            title="Edit"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          <button
            onClick={() => onToggleHidden(broadcast.id, !broadcast.is_hidden)}
            className={`p-1 ${broadcast.is_hidden ? 'text-yellow-500 hover:text-white' : 'text-zinc-500 hover:text-yellow-500'}`}
            title={broadcast.is_hidden ? 'Show to public' : 'Hide from public'}
            data-testid={`toggle-hidden-${broadcast.id}`}
          >
            {broadcast.is_hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
          <button 
            onClick={() => onDelete(broadcast.id)} 
            className="p-1 text-zinc-500 hover:text-red-500"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          <button className="p-1 text-zinc-500">
            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>
      
      {/* Inline Expanded Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 py-4 bg-zinc-800/50 border-t border-white/5">
              <VODPanel
                broadcast={broadcast}
                vodForm={vodForm}
                setVodForm={setVodForm}
                savingVod={savingVod}
                onSaveVod={onSaveVod}
                onSyncScores={onSyncScores}
                syncingScores={syncingScores}
                onArchive={onArchiveToOnDemand}
                archiving={archiving}
                onRefreshBroadcast={onRefreshBroadcast}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompactBroadcastRow;
